import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useOrg } from '../app/OrgProvider';
import { useAuth } from '../auth/AuthProvider';
import { track } from '../lib/amplitude';
import { errorMessage } from '../lib/api';
import type { PendingInvite } from '../lib/types';
import { qk } from '../queries/keys';
import { acceptInvite, fetchMyInvites, roleLabel } from '../queries/orgs';
import { AppLink } from './Nav';
import { UsersIcon } from './icons';
import { Button, ErrorText } from './ui';

/**
 * Invitations waiting for this account, shown inside the app.
 *
 * People sign up on their own before the invitation reaches them, and the
 * email then sits unopened: they believe they are in, the team sees them as
 * still invited, and the seat held for them goes unused. Every sign that
 * anything is unfinished was in a mailbox they had closed. This puts it on
 * the page they are already looking at, with the one button that finishes
 * it, and a link to the full page for anyone who wants to read what joining
 * changes first.
 */

/** Why the account cannot take the invitation yet, in the person's own terms. */
function blockerText(invite: PendingInvite): string | null {
  const { blocker, org } = invite;
  if (!blocker) return null;
  if (blocker.code === 'has_subscription') {
    return `You own ${blocker.orgName}, and its plan is running. Cancel that subscription, or hand the team to someone else and leave it, before joining ${org.name}.`;
  }
  return `You are on ${blocker.orgName}. A person is on one team at a time, so leave it, or ask an admin there to remove you, before joining ${org.name}.`;
}

function InviteRow({ invite }: { invite: PendingInvite }) {
  const queryClient = useQueryClient();
  const { refetch } = useOrg();
  const [joined, setJoined] = useState(false);

  const accept = useMutation({
    mutationFn: () => acceptInvite(invite.token),
    onSuccess: (result) => {
      track('Invite Accepted', { role: result.role, from: 'pending-banner' });
      setJoined(true);
      // The team is now this person's: its name in the header, its library
      // on the page, its plan behind the account.
      refetch();
      void queryClient.invalidateQueries({ queryKey: ['me'] });
      void queryClient.invalidateQueries({ queryKey: ['videos'] });
      void queryClient.invalidateQueries({ queryKey: qk.myInvites() });
    },
  });

  const blocked = blockerText(invite);

  return (
    <div className="flex flex-col gap-3 rounded-md border border-hairline bg-slate-900 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 gap-3">
        <UsersIcon size={18} className="mt-0.5 shrink-0 text-chalk-faint" />
        <div className="min-w-0 space-y-0.5">
          <p className="text-body font-medium text-chalk">
            {joined ? `You are on ${invite.org.name}` : `You have been invited to ${invite.org.name}`}
          </p>
          <p className="text-caption text-chalk-dim">
            {joined ? (
              'Everything you record from now on lands in its library.'
            ) : (
              <>
                {invite.invitedBy ? (
                  <>
                    <span className="text-chalk">{invite.invitedBy}</span> invited you
                  </>
                ) : (
                  'You were invited'
                )}{' '}
                as {roleLabel(invite.role).toLowerCase()}
                {invite.seat === 'paid' ? ', on a seat the team pays for' : ', on a free seat'}.
                {blocked ? ` ${blocked}` : ''}
              </>
            )}
          </p>
          {accept.isError ? (
            <div className="pt-1">
              <ErrorText>{errorMessage(accept.error)}</ErrorText>
            </div>
          ) : null}
        </div>
      </div>
      {joined ? null : (
        <div className="flex shrink-0 items-center gap-2 sm:pl-4">
          <AppLink
            to="/invite/$token"
            params={{ token: invite.token }}
            className="text-caption text-chalk-faint transition-colors duration-120 hover:text-chalk-dim"
          >
            {blocked ? 'See the invitation' : 'What changes'}
          </AppLink>
          {blocked ? null : (
            <Button variant="primary" size="md" busy={accept.isPending} onClick={() => accept.mutate()}>
              {accept.isError ? 'Try again' : `Join ${invite.org.name}`}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export function PendingInvites() {
  const { user } = useAuth();
  const invites = useQuery({
    queryKey: qk.myInvites(),
    queryFn: fetchMyInvites,
    enabled: Boolean(user),
    staleTime: 60_000,
  });

  const open = invites.data?.invites ?? [];
  if (open.length === 0) return null;

  return (
    <div className="mb-6 grid gap-2" role="status">
      {open.map((invite) => (
        <InviteRow key={invite.id} invite={invite} />
      ))}
    </div>
  );
}
